'use client';
import * as React from 'react';
import * as Yup from 'yup';

import { Formik, Form } from 'formik';
import { Input, PhoneInput } from '@repo/ui/src/components/Form';

import { identify } from '@repo/ui/src/utils/identify';
import { captureMessage } from '@sentry/nextjs';

import '@repo/ui/src/utils/phoneValidation';
import Button from '@repo/ui/src/components/Button';
import { useEmailSignup } from '@repo/ui/src/hooks/customer/useEmailSignup';
import { classNames } from '@repo/ui/src/utils/classNames';
import { MarketingSignupProps } from '@repo/ui/src/types/marketingSignup';
import { ConnectedFocusError } from 'focus-formik-error';

export interface Props {
  darkMode?: boolean;
  emailSignup?: MarketingSignupProps;
  smsSignup?: MarketingSignupProps;
  submitButton: string;
  sourceDetail?: string;
  success?: string;
  failure?: string;
  fullWidth?: boolean;
  center?: boolean;
  // Dependency injection for traeger-specific components and hooks
  Input?: React.ComponentType<any>;
  PhoneInput?: React.ComponentType<any>;
  useEmailSignup?: () => {
    trigger: (data: any) => Promise<any>;
    isMutating: boolean;
    data?: any;
    error?: any;
  };
  phoneValidationImport?: () => void;
}

const MarketingCaptureForm = ({
  emailSignup,
  smsSignup,
  darkMode,
  submitButton,
  success,
  failure,
  sourceDetail,
  fullWidth,
  center,
  Input: InjectedInput,
  PhoneInput: InjectedPhoneInput,
  useEmailSignup: injectedUseEmailSignup,
  phoneValidationImport,
}: Props) => {
  const defaultHookResult = useEmailSignup();
  const injectedHookResult = injectedUseEmailSignup ? injectedUseEmailSignup() : null;
  const { trigger, isMutating: isSubmitting, data, error } = injectedHookResult || defaultHookResult;
  const submitted = !!data;

  // Use injected components when available, fallback to defaults
  const InputComponent = InjectedInput || Input;
  const PhoneInputComponent = InjectedPhoneInput || PhoneInput;

  // Handle phone validation import
  React.useEffect(() => {
    if (phoneValidationImport) {
      phoneValidationImport();
    }
  }, [phoneValidationImport]);
  const buttonText = submitButton || 'Submit';
  const validationSchema = Yup.object().shape({
    email: Yup.string().required().email(emailSignup?.failure),
    sms: Yup.string().phone(smsSignup?.failure),
  });
  
  return (
    <div className={classNames(fullWidth && 'tw-w-full')}>
      <Formik
        initialValues={{ email: '', sms: '' }}
        validationSchema={validationSchema}
        onSubmit={async (values: { email: string; sms: string }) => {
          try {
            const emailSubscribe = values?.email ? !!values?.email : undefined;
            const phoneNumber = values?.sms;
            const smsSubscribe = values?.sms ? !!values?.sms : undefined;

            await trigger({
              email: values?.email,
              phoneNumber,
              smsSubscribe,
              sourceDetail,
              emailSubscribe,
            });
            identify({ email: values?.email });
          } catch {
            captureMessage('track marketing form failed to submit', {
              level: 'error',
              user: { email: values.email },
            });
          }
        }}
      >
        {() => {
          return (
            <Form className={classNames('tw-grid tw-gap-y-8', darkMode && 'tw-dark')} role="form">
              <ConnectedFocusError />

              {emailSignup?.label && (
                <InputComponent
                  label={emailSignup?.label}
                  required
                  placeholder={emailSignup?.placeholder}
                  disabled={isSubmitting || submitted}
                  type="email"
                  name="email"
                  id={`email_submit_${sourceDetail}`}
                  autoComplete="email"
                  data-testid="marketing-capture-email"
                  darkBackground={darkMode}
                />
              )}
              {smsSignup?.label && (
                <PhoneInputComponent
                  name="sms"
                  type="text"
                  disabled={isSubmitting || submitted}
                  placeholder={smsSignup?.placeholder}
                  label={smsSignup?.label}
                  required={smsSignup?.required}
                  autoComplete="tel"
                  darkBackground={darkMode}
                />
              )}
              {error && (
                <p
                  className={classNames(darkMode ? 'tw-text-warm-200' : 'tw-text-neutral-500')}
                  id={failure}
                >
                  {failure}
                </p>
              )}
              {submitted && (
                <p className={classNames(darkMode ? 'tw-text-warm-200' : 'tw-text-neutral-500')}>
                  {success}
                </p>
              )}
              <Button
                disabled={isSubmitting || (!error && submitted)}
                type="submit"
                aria-label={buttonText}
                className={classNames(center && 'tw-mx-auto tw-max-w-[200px]')}
                data-testid="marketing-capture-submit"
              >
                {buttonText}
              </Button>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default MarketingCaptureForm;
